
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { CountryList } from './CountryList';
import { LeagueList } from './LeagueList';
import { Country, League, GroupedLeagues } from './types';

const CountryOrderManager = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch countries
      const { data: countriesData, error: countriesError } = await supabase
        .from('countries')
        .select('id, name, display_order')
        .order('display_order');

      if (countriesError) throw countriesError;
      setCountries(countriesData || []);

      // Fetch leagues with display_order
      const { data: leaguesData, error: leaguesError } = await supabase
        .from('leagues')
        .select('id, name, country, display_order')
        .order('display_order');

      if (leaguesError) throw leaguesError;
      
      // Ensure all leagues have a display_order value
      const processedLeagues = (leaguesData || []).map((league, index) => ({
        ...league,
        display_order: league.display_order || index + 1
      }));
      
      setLeagues(processedLeagues);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (result: any, type: 'countries' | 'leagues') => {
    if (!result.destination) return;

    const items = type === 'countries' ? [...countries] : [...leagues];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index + 1,
    }));

    if (type === 'countries') {
      setCountries(updatedItems as Country[]);
    } else {
      setLeagues(updatedItems as League[]);
    }
  };

  const saveOrder = async (type: 'countries' | 'leagues') => {
    try {
      const items = type === 'countries' ? countries : leagues;
      const tableName = type === 'countries' ? 'countries' : 'leagues';

      for (const item of items) {
        const { error } = await supabase
          .from(tableName)
          .update({ display_order: item.display_order })
          .eq('id', item.id);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `${type === 'countries' ? 'Country' : 'League'} order has been updated`,
      });
      
      queryClient.invalidateQueries({ queryKey: [tableName] });
      
      await fetchData();
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: "Error",
        description: `Failed to save ${type === 'countries' ? 'country' : 'league'} order`,
        variant: "destructive",
      });
    }
  };

  // Group leagues by country
  const groupedLeagues: GroupedLeagues = leagues.reduce((acc, league) => {
    if (!acc[league.country]) {
      acc[league.country] = [];
    }
    acc[league.country].push(league);
    return acc;
  }, {} as GroupedLeagues);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Tabs defaultValue="countries">
        <TabsList className="mb-4">
          <TabsTrigger value="countries">Countries</TabsTrigger>
          <TabsTrigger value="leagues">Leagues</TabsTrigger>
        </TabsList>

        <TabsContent value="countries">
          <CountryList 
            countries={countries}
            onDragEnd={(result) => handleDragEnd(result, 'countries')}
            onSave={() => saveOrder('countries')}
          />
        </TabsContent>

        <TabsContent value="leagues">
          <LeagueList 
            groupedLeagues={groupedLeagues}
            onDragEnd={(result) => handleDragEnd(result, 'leagues')}
            onSave={() => saveOrder('leagues')}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CountryOrderManager;
